import { Navigate, useLocation } from 'react-router-dom'
import { SESSION_STATUS, useSession } from '../lib/sessionContext'
import PageLoader from '../components/PageLoader'
import { ErrorState } from '../components/States'
import Button from '../components/Button'
import { shell as S } from './strings'

/**
 * Re-applies the server's force-redirection rules to client-side navigation.
 *
 * `ForceRedirectionMiddleware` answers 302 to a user whose account has expired, who is
 * awaiting approval, whose profile is incomplete or who has not accepted the terms. That
 * covers the first request — but once this app is running, moving between screens never
 * touches the server, so without this a gated user would simply keep browsing.
 *
 * The middleware is not modified and its logic is not moved: `GET /api/session` reports
 * the same verdict as `Gate`, and this routes on it. A parity test keeps the two sides
 * from drifting.
 */

/** `Gate.RedirectTo` is a legacy server path; these are the screens they became. */
const GATE_ROUTES = {
  Expired: '/activate/expired',
  Resigned: '/activate/resigned',
  WaitingUser: '/activate/waiting-user',
  WaitingAdmin: '/activate/waiting-admin',
  Suspended: '/activate/suspended',
  // The server 302s /Redirect to the profile page; that is the screen the user must
  // finish before anything else opens.
  ProfileIncomplete: '/portal/profile',
  TermsOfUse: '/portal/terms',
}

/** Reachable while gated: sign-out has to work, and errors have to be readable. */
const ALWAYS_ALLOWED = ['/login', '/error', '/unauthorized', '/shutdown']

export default function SessionGate({ children }) {
  const { status, gate, reload } = useSession()
  const { pathname } = useLocation()

  // Nothing is rendered against an unknown session: a flash of the dashboard for
  // someone who turns out to be signed out is exactly what this exists to prevent.
  if (status === SESSION_STATUS.loading) return <PageLoader full />

  if (status === SESSION_STATUS.anonymous) {
    return pathname === '/login' ? children : <Navigate to="/login" replace />
  }

  /* A server that cannot answer is not a sign-out. In development it is usually just
     the API not running, and every screen is still on mock data, so the app carries on
     against the static session; in production the user is told, and offered a retry. */
  if (status === SESSION_STATUS.error) {
    if (import.meta.env.DEV) return children

    return (
      <ErrorState
        title={S.sessionErrorTitle}
        description={S.sessionErrorDesc}
        action={<Button onClick={reload}>{S.retry}</Button>}
      />
    )
  }

  const destination = gate ? GATE_ROUTES[gate.Required] : null

  if (destination && pathname !== destination && !ALWAYS_ALLOWED.includes(pathname)) {
    return <Navigate to={destination} replace />
  }

  return children
}
